"use client";

import type { IImage } from "@components/types/media";
import type { ICmsFloatingMediaItem } from "@mw/shared/payload-types";
import type React from "react";

import { LazyVideoPlayer } from "@components/core/LazyVideoPlayer";
import { PreviewPlayerButton } from "@components/core/PreviewPlayer";
import { isValidImage } from "@ui/lib/image";
import { sizes } from "@ui/lib/sizes";
import { getVideoUrl } from "@utilities/payload";
import { clsx } from "clsx";
import { useTranslations } from "next-intl";

type CardSize = "sm" | "md" | "lg";

// Scatter slots transcribed from the Figma "Big Carousel" frame (1728×915), as percentages of
// the desktop container so the arrangement scales with its width. Each video fills the slot at
// its CMS array index (capped at this count via the block's maxRows).
const SLOTS: { size: CardSize; left: number; top: number; width: number; height: number }[] = [
  { size: "sm", left: 38.48, top: 13.83, width: 5.79, height: 13.88 },
  { size: "md", left: 17.65, top: 20.82, width: 10.42, height: 24.81 },
  { size: "lg", left: 42.53, top: 42.13, width: 14.47, height: 34.43 },
  { size: "sm", left: 59.09, top: 22.9, width: 5.79, height: 13.88 },
  { size: "sm", left: 3.65, top: 52.4, width: 5.79, height: 13.88 },
  { size: "sm", left: 90.05, top: 32.95, width: 5.79, height: 13.88 },
  { size: "md", left: 74.65, top: 11.64, width: 10.42, height: 24.81 },
  { size: "md", left: 71.18, top: 55.14, width: 10.42, height: 24.81 },
];

const COVER_SIZES = sizes("10rem", { lg: "15vw" });

type FloatingMediaProps = {
  title?: string | null;
  description?: string | null;
  videos?: ICmsFloatingMediaItem;
};

export function FloatingMedia({ title, description, videos }: FloatingMediaProps) {
  // Keep each card's CMS array index as its scatter slot, so an unresolvable video leaves its
  // slot empty instead of shifting later cards into earlier Figma positions.
  const cards = (videos ?? []).flatMap((item, slotIndex) => {
    const slot = SLOTS[slotIndex];
    const videoUrl = getVideoUrl(item.media);
    if (!slot || !videoUrl) return [];
    return [{ slotIndex, slot, videoUrl, cover: { resource: item.coverImage, sizes: COVER_SIZES } }];
  });

  // Keep the heading/copy even if every video fails to resolve; only bail when nothing would render.
  if (!cards.length && !title && !description) return null;

  // One DOM tree serves both breakpoints: below `lg` a horizontal snap-scroll row; at `lg` an
  // absolutely-positioned scatter, with slot percentages fed in as CSS vars consumed only by `lg:`.
  return (
    <div className='lg:tw-relative lg:tw-mx-auto lg:tw-aspect-[1728/915] lg:tw-w-full lg:tw-overflow-hidden lg:tw-@container'>
      {title ? (
        <p
          aria-hidden
          className='tw-absolute tw-left-[51.16%] tw-top-[47.87%] tw-hidden -tw-translate-x-1/2 -tw-translate-y-1/2 tw-whitespace-nowrap tw-text-center tw-font-display tw-leading-none tw-text-black/10 lg:tw-block lg:tw-text-[9.178cqw] lg:tw-tracking-[-0.275cqw]'
        >
          {title}
        </p>
      ) : null}

      {title || description ? (
        <div className='tw-mb-6 tw-flex tw-flex-col tw-gap-4 lg:tw-absolute lg:tw-left-[18.98%] lg:tw-top-[61.26%] lg:tw-w-[19.68%]'>
          {title ? <h3 className='tw-type-2xl tw-text-black lg:tw-type-base'>{title}</h3> : null}
          {description ? <p className='tw-type-base tw-text-black/60'>{description}</p> : null}
        </div>
      ) : null}

      <ul className='tw-flex tw-snap-x tw-list-none tw-gap-3 tw-overflow-x-auto tw-scrollbar-none tw-px-1 lg:tw-absolute lg:tw-inset-0 lg:tw-m-0 lg:tw-block lg:tw-overflow-visible lg:tw-p-0'>
        {cards.map(({ slotIndex, slot, videoUrl, cover }) => (
          <li
            key={slotIndex}
            className='tw-w-40 tw-shrink-0 tw-snap-start lg:tw-absolute lg:tw-left-[var(--fm-l)] lg:tw-top-[var(--fm-t)] lg:tw-h-[var(--fm-h)] lg:tw-w-[var(--fm-w)]'
            style={
              {
                "--fm-l": `${slot.left}%`,
                "--fm-t": `${slot.top}%`,
                "--fm-w": `${slot.width}%`,
                "--fm-h": `${slot.height}%`,
              } as React.CSSProperties
            }
          >
            <MediaCard cover={cover} size={slot.size} videoUrl={videoUrl} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function MediaCard({ cover, size, videoUrl }: { cover: IImage; size: CardSize; videoUrl: string }) {
  return (
    <div
      className={clsx(
        "tw-relative tw-aspect-[4/5] tw-w-full tw-overflow-hidden tw-rounded-xl lg:tw-aspect-auto lg:tw-h-full",
        // Figma rounds the small and large cards to 16px; only the medium cards stay at 12px.
        size !== "md" && "lg:tw-rounded-2xl",
      )}
    >
      <LazyVideoPlayer
        controls={false}
        cover={cover}
        fill
        fit='cover'
        // Defer media I/O to the first play click so vidstack doesn't fetch metadata for all
        // cards when the section scrolls in; if the poster didn't resolve, fall back to the
        // default strategy so a first video frame still shows.
        load={isValidImage(cover) ? "play" : undefined}
        playButton={({ isPlaying, togglePlay }) => (
          <PlayBadge isPlaying={isPlaying} size={size} togglePlay={togglePlay} />
        )}
        src={videoUrl}
      />
    </div>
  );
}

// Figma centres the badge on small cards and moves it to the bottom-right corner on medium/large.
const LG_BADGE_POSITION: Record<CardSize, string> = {
  sm: "lg:tw-bottom-[0.694cqw]",
  md: "lg:tw-bottom-[0.81cqw] lg:tw-left-auto lg:tw-right-[0.81cqw] lg:tw-translate-x-0",
  lg: "lg:tw-bottom-[1.736cqw] lg:tw-left-auto lg:tw-right-[1.736cqw] lg:tw-translate-x-0",
};

function PlayBadge({
  size,
  isPlaying,
  togglePlay,
}: {
  size: CardSize;
  isPlaying: boolean;
  togglePlay: (event: React.MouseEvent<HTMLButtonElement>) => void;
}) {
  const t = useTranslations("ClientKeys.Globals");

  return (
    <button
      aria-label={isPlaying ? t("pause") : t("play")}
      className={clsx(
        "tw-group/preview-button tw-pointer-events-auto tw-absolute tw-bottom-3 tw-left-1/2 -tw-translate-x-1/2 tw-cursor-pointer tw-appearance-none tw-rounded-full tw-border-none tw-bg-transparent tw-p-0 tw-outline-none focus-visible:tw-outline-focus",
        LG_BADGE_POSITION[size],
      )}
      onClick={togglePlay}
      type='button'
    >
      <PreviewPlayerButton isActive={isPlaying} showRing={false} size='sm' />
    </button>
  );
}
